import Decimal from "decimal.js";

import { D_1, D0, D1, D2 } from "../constants";
import { ORIGIN, transform2DVectors as transform2DVectors, VectorD3 } from "../vector";
import { WCircleCollider } from "./circle";
import { Box, WCollider } from "./collider";

export class WPolygonCollider extends WCollider {

  localPoints: VectorD3[];
  points: VectorD3[];

  constructor(
    localPoints: VectorD3[],
    location = ORIGIN,
    rotation = D0
  ) {
    super(location, rotation);
    this.localPoints = localPoints;
    this.points = [];

    for(const p of this.localPoints) this.points.push(p.clone());

    this.updatePoints();
  }

  // Misc

  clone(): WCollider {
    return new WPolygonCollider(this.localPoints, this.location, this.rotation);
  }

  // Position

  updatePoints(): void {
    this.points = transform2DVectors(this.localPoints, this.location, this.rotation);
  }

  // Collisions

  contains(collider: WCollider): boolean {
    if(collider instanceof WPolygonCollider) {
      // For each edge of this polygon
      for(let i = 0; i < this.points.length; ++i) {
        const point = this.points[i];
        const edge = VectorD3.sub(this.point(i + 1), point);

        // Check if all points of the other polygon are on the left side of this edge
        for(const p of collider.points) {
          const edgeStartToPoint = VectorD3.sub(p, point);
          const cross = edgeStartToPoint.x.mul(edge.y).minus(edgeStartToPoint.y.mul(edge.x));

          // If any point is on the right side, this polygon is not contained
          if(cross.lessThan(D0)) return false;
        }
      }

      // If all edges have all points on the left side, the polygon is contained
      return true;
    }

    if(collider instanceof WCircleCollider) {
      const { location, radius } = collider;

      // For each edge of this polygon
      for(let i = 0; i < this.points.length; ++i) {
        const point = this.points[i];
        const edge = VectorD3.sub(this.point(i + 1), point);

        // Check if the circle is entirely on the left side of this edge
        const edgeStartToCircle = VectorD3.sub(location, point);
        const cross = edgeStartToCircle.x.mul(edge.y).minus(edgeStartToCircle.y.mul(edge.x));

        // If the circle center is on the right side or too close to the edge, the circle is not contained
        if(cross.lessThanOrEqualTo(radius)) return false;
      }

      // If all edges have the circle entirely on the left side, the circle is contained
      return true;
    }

    return collider.isContained(this);
  }

  isContained(collider: WCollider): boolean {
    if(collider instanceof WPolygonCollider) return collider.contains(this);

    if(collider instanceof WCircleCollider) {
      const { location, radius } = collider;
      const r2 = radius.mul(radius);

      // If there is a point outside the circle, this is not contained
      for(const p of this.points) if(p.distanceToSquared(location).greaterThan(r2)) return false;

      return true;
    }

    return collider.contains(this);
  }

  overlaps(collider: WCollider): boolean {
    if(this.localPoints.length < 1) return false;

    if(collider instanceof WPolygonCollider) return ! this.hasSeparatingAxisWithPolygon(collider) && ! collider.hasSeparatingAxisWithPolygon(this);

    if(collider instanceof WCircleCollider) return ! this.hasSeparatingAxisWithCircle(collider);

    return collider.overlaps(this);
  }

  hasSeparatingAxisWithPolygon(collider: WPolygonCollider) {
    const points1 = this.points;
    const points2 = collider.points;

    for(let i = 0; i < points1.length; ++i) {
      const point = points1[i];
      //const edge = VectorD3.sub(points1[(i + 1) % points1.length], point);
      const edge = this.edge(i);
      let cross = D_1;

      for(const p of points2) {
        const edgeStartToPoint = VectorD3.sub(p, point);


        cross = edgeStartToPoint.x.mul(edge.y).minus(edgeStartToPoint.y.mul(edge.x));
        // If there's a point of the other collider that's crossing this edge,
        // this edge is not a separating axis, skip
        if(cross.lessThan(D0)) break;

      }

      // this edge is a separating axis
      if(cross.greaterThanOrEqualTo(D0)) return true;
    }

    return false;
  }

  hasSeparatingAxisWithCircle(circle: WCircleCollider): boolean {
    const { points } = this;
    const { location, radius } = circle;

    // center of circle needs to be out of the polygon
    if(this.isPointInside(location))
      return false;

    const distances = [];
    // check intersection of the circle with every edge
    for(let i = 0; i < points.length; ++i) {
      const p1 = this.point(i);
      const p2 = this.point(i + 1);
      const edge = this.edge(i).normalize();

      const d = this.pointToLineDistance(p1, p2, location);
      distances.push(d);

      // we are not intersecting if the center is far away from the line of the edge
      if(d.greaterThanOrEqualTo(radius)) continue;

      const l = radius.pow(D2).sub(d.pow(D2)).sqrt();
      const p1_on_edge = p1.dot(edge);
      const p2_on_edge = p2.dot(edge);
      const c_on_edge = location.dot(edge);

      // if p1 and p2 are both outside the circle we are not intersecting
      if(c_on_edge.add(l).lessThanOrEqualTo(p1_on_edge)) continue;
      if(c_on_edge.sub(l).greaterThanOrEqualTo(p2_on_edge)) continue;

      // we are intersecting with one edge, so can't have separating axis
      return false;
    }



    return true;
  }


  pointToLineDistance(p1: VectorD3, p2: VectorD3, point: VectorD3): Decimal {
    // TODO: returning the difference between projection (dot) of p1 and point on edge normal could be faster
    const divisor = p2.y.sub(p1.y).pow(D2).add(p2.x.sub(p1.x).pow(D2)).sqrt();
    const numerator = p2.y.sub(p1.y).mul(point.x).sub(p2.x.sub(p1.x).mul(point.y)).add(p2.x.mul(p1.y)).sub(p2.y.mul(p1.x)).abs();
    return numerator.div(divisor);
  }

  // Internal
  isPointInside(point: VectorD3): boolean {
    // if the positive horizontal half-line that starts from the point has 1 intersection (not 0 or 2) with the polygon
    // the point is inside
    let intersections = 0;

    // check intersection with every edge of the polygon
    for(let i = 0; i < this.points.length; ++i) {
      const p1 = this.point(i);
      const p2 = this.point(i + 1);

      //check if half-line intersects this edge 
      if(p1.y.greaterThanOrEqualTo(point.y) && p2.y.greaterThanOrEqualTo(point.y))
        continue;
      else if(p1.y.lessThanOrEqualTo(point.y) && p2.y.lessThanOrEqualTo(point.y))
        continue;
      else if(p1.x.lessThanOrEqualTo(point.x) && p2.x.lessThanOrEqualTo(point.x))
        continue;
      else {
        const h_edge = p2.y.sub(p1.y); // projection of edge on y axis
        const h_p1_half_line = point.y.sub(p1.y); // height of p1 from half line
        const b_edge = p2.x.sub(p1.x); // projection of edge on x axis
        const b_cut_by_line = h_p1_half_line.div(h_edge).mul(b_edge);
        const intersection_x = p1.x.add(b_cut_by_line);
        if(intersection_x.greaterThan(point.x)) intersections += 1;
      }
    }

    return intersections === 1;
  }

  createExpanded(distance: Decimal) {
    const localPoints: VectorD3[] = [];

    for(let i = 0; i < this.localPoints.length; ++i) {
      const expand = this.normal(i).multiplyScalar(distance);
      const nextPoint = this.point(i + 1);

      localPoints.push(VectorD3.add(expand, this.point(i)));
      localPoints.push(VectorD3.add(expand, nextPoint));

      const nextExpand = this.normal(i + 1).multiplyScalar(distance);
      const { stepAngle, steps } = this.getRotationSteps(expand, nextExpand, distance, D1);
      const { x, y } = expand;

      for(let step = D1; step.lessThanOrEqualTo(steps.sub(Decimal(1))); step = step.plus(D1)) {
        const angle = stepAngle.mul(step);
        const cos = angle.cos();
        const sin = angle.sin();
        const filler = new VectorD3(x.mul(cos).minus(y.mul(sin)), x.mul(sin).plus(y.mul(cos)));

        localPoints.push(filler.add(nextPoint));
      }
    }
    const poly = new WPolygonCollider(localPoints);
    poly.setLocationAndRotation(this.location, this.rotation);
    return poly;
  }

  getRotationSteps(from: VectorD3, to: VectorD3, radius: Decimal, maxLength: Decimal) {
    const angle = from.angleTo(to);
    let steps = D0;
    let rope: Decimal;
    let stepAngle: Decimal;

    do {
      steps = steps.plus(D1);
      stepAngle = angle.div(steps);
      rope = radius.mul(D2).mul(stepAngle.div(D2).sin());
    } while(rope.greaterThan(maxLength));

    return { stepAngle, steps };
  }

  normal(index: number) {
    const normal = this.edge(index);

    normal.setXY(normal.y, normal.x.negated());

    return normal.normalize();
  }

  localPoint(index: number) {
    return this.localPoints[index % this.localPoints.length].clone();
  }

  point(index: number) {
    return this.points[index % this.points.length].clone();
  }

  localEdge(index: number) {
    return VectorD3.sub(this.localPoint(index + 1), this.localPoint(index));
  }

  edge(index: number) {
    return VectorD3.sub(this.point(index + 1), this.point(index));
  }

  getBox() {
    let left: Decimal;
    let right: Decimal;
    let top: Decimal;
    let bottom: Decimal;

    left = right = this.points[0].x;
    top = bottom = this.points[0].y;

    for(let i = 1; i < this.points.length; i++) {
      left = Decimal.min(left, this.points[i].x);
      right = Decimal.max(right, this.points[i].x);
      top = Decimal.min(top, this.points[i].y);
      bottom = Decimal.max(bottom, this.points[i].y);
    }

    return new Box(left, right, top, bottom);
  }

  getPoints(): VectorD3[] {
    return this.points;
  }

  setLocalPoints(points: VectorD3[]) {
    this.localPoints = points;
    this.updatePoints();
  }

  getLocalPoints(): VectorD3[] {
    return this.localPoints;
  }

  distanceTo(_collider: WCollider): Decimal {
    throw new Error("Method not implemented.");
  }
}
